/**
 * Google Ads (gtag) conversion helpers.
 *
 * The base gtag.js loader is in /public/index.html and configures the
 * account `AW-18432343798`. This module fires TWO events on every
 * WhatsApp click so both custom-event and classic-conversion setups
 * work simultaneously in Google Ads:
 *
 *   1. `whatsapp_click` — custom event (usable by anyone creating a
 *      "Custom" conversion action targeting the event name).
 *   2. `conversion` with send_to = AW-18432343798/<LABEL> — the classic
 *      Google Ads conversion event that Tag Assistant expects. The
 *      label defaults to the WhatsApp-click conversion action set up
 *      for kanbr.com and can be overridden by REACT_APP_AW_CONVERSION_LABEL.
 */

const AW_ID = 'AW-18432343798';
// Default conversion action label — outbound-click conversion for
// WhatsApp CTAs on www.kanbr.com. Override at build time with
// REACT_APP_AW_CONVERSION_LABEL if the label ever changes.
const DEFAULT_LABEL = 'xWbCOWPje8cEPb9nNVE';

function safeGtag(...args) {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    try { window.gtag(...args); } catch { /* no-op */ }
}

function resolveSendTo() {
    const envLabel = process.env.REACT_APP_AW_CONVERSION_LABEL;
    const label = envLabel || DEFAULT_LABEL;
    if (!label) return null;
    return label.startsWith('AW-') ? label : `${AW_ID}/${label}`;
}

/**
 * Fire a WhatsApp-click conversion event.
 * @param {Object} opts
 * @param {string} [opts.source] - Location on the site (e.g. "hero", "job-work", "cart").
 * @param {string} [opts.message] - Pre-filled WhatsApp message (first 80 chars logged).
 * @param {Function} [opts.eventCallback] - Optional callback fired after Google Ads acks the beacon.
 */
export function trackWhatsAppClick({ source, message, eventCallback } = {}) {
    // Custom event — useful if the conversion action in Google Ads is
    // set up as a Custom event targeting `whatsapp_click`.
    safeGtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: source || 'unknown',
        page_location: typeof window !== 'undefined' ? window.location.pathname : undefined,
        message_preview: (message || '').slice(0, 80),
    });

    // Classic Google Ads conversion event — this is what Tag Assistant
    // looks for and what most Google Ads conversion actions expect.
    const sendTo = resolveSendTo();
    if (sendTo) {
        safeGtag('event', 'conversion', {
            send_to: sendTo,
            event_callback: typeof eventCallback === 'function' ? eventCallback : undefined,
        });
    } else if (typeof eventCallback === 'function') {
        // No conversion configured, but caller still wants their callback.
        eventCallback();
    }
}

/**
 * Opens WhatsApp (in a new tab) AND fires the gtag conversion in one call.
 * Use this instead of `window.open(buildWhatsAppUrl(...))` at every CTA.
 */
export function openWhatsAppWithTracking(url, opts = {}) {
    trackWhatsAppClick(opts);
    if (typeof window !== 'undefined' && url) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
}
